/**
 * PlayFirst Lite — API client (no auth, no cookies; stateless).
 * Endpoints under /api/lite/*.
 */

const API = process.env.REACT_APP_BACKEND_URL;

async function postJson(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail?.message || data?.detail || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

async function getJson(path) {
  const res = await fetch(`${API}${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || 'Request failed');
  return data;
}

// Helpers to attach the Lite Pro magic-link token to every generate call so
// paid users bypass the daily quota.
const LITE_PRO_TOKEN_KEY = 'playfirst_lite_pro_token';
const LITE_EMAIL_KEY = 'playfirst_lite_email';

function readProToken() {
  try { return localStorage.getItem(LITE_PRO_TOKEN_KEY) || null; } catch { return null; }
}

function attachProAuth(payload) {
  const token = readProToken();
  if (token && !payload.pro_token) payload.pro_token = token;
  if (token && !payload.email) {
    try { payload.email = localStorage.getItem(LITE_EMAIL_KEY) || payload.email; } catch {/* ignore */}
  }
  return payload;
}

export const liteApi = {
  geo: () => getJson('/api/lite/geo'),
  usage: (email) => getJson(`/api/lite/usage${email ? `?email=${encodeURIComponent(email)}` : ''}`),
  deidentify: (text) => postJson('/api/lite/deidentify', { text }),
  generateReport: (payload) => postJson('/api/lite/generate/report', attachProAuth({ ...payload })),
  generateParent: (payload) => postJson('/api/lite/generate/parent', attachProAuth({ ...payload })),
  generateGlp: (payload) => postJson('/api/lite/generate/glp', attachProAuth({ ...payload })),
  generateActivity: (payload) => postJson('/api/lite/generate/activity', attachProAuth({ ...payload })),
  generateSessionBridge: (payload) => postJson('/api/lite/generate/session-bridge', attachProAuth({ ...payload })),
  emailUnlock: (email) => postJson('/api/lite/email-unlock', { email }),
  waitlist: (email, region = 'US') => postJson('/api/lite/waitlist', { email, region }),
  extractStyle: (samples) => postJson('/api/lite/extract-style', { samples, confirm_deidentified: true }),
  emailReport: (payload) => postJson('/api/lite/email-report', attachProAuth({ ...payload })),
  emailParentHandoff: (payload) => postJson('/api/lite/email-parent-handoff', attachProAuth({ ...payload, slp_email: payload.slp_email || (function(){ try{return localStorage.getItem('playfirst_lite_email')||null;}catch{return null;}})() })),
  trackHandout: (payload) => postJson('/api/lite/track-handout', attachProAuth({ ...payload })),
  feedback: (payload) => postJson('/api/lite/feedback', payload),
  wishlist: (payload) => postJson('/api/lite/wishlist', payload),
  // Lite Pro paywall
  checkoutLitePro: ({ email, billing_period = 'monthly', currency = 'ZAR' }) =>
    postJson('/api/lite/checkout', { email, billing_period, currency, origin_url: window.location.origin }),
  verifyLitePro: (reference) => getJson(`/api/lite/verify/${encodeURIComponent(reference)}`),
  checkLitePro: ({ email, token }) => postJson('/api/lite/check-pro', { email, token }),
};

export const litePro = {
  TOKEN_KEY: LITE_PRO_TOKEN_KEY,
  EMAIL_KEY: LITE_EMAIL_KEY,
  isActive() {
    try { return !!localStorage.getItem(LITE_PRO_TOKEN_KEY); } catch { return false; }
  },
  setActive(email, token, expiresAt) {
    try {
      localStorage.setItem(LITE_PRO_TOKEN_KEY, token);
      localStorage.setItem(LITE_EMAIL_KEY, email);
      if (expiresAt) localStorage.setItem('playfirst_lite_pro_expires', expiresAt);
    } catch {/* ignore */}
  },
  clear() {
    try {
      localStorage.removeItem(LITE_PRO_TOKEN_KEY);
      localStorage.removeItem('playfirst_lite_pro_expires');
    } catch {/* ignore */}
  },
};

export default liteApi;
